import React, { FormEvent, useState } from 'react';
import Order from '../../../models/Order';
import Pagination from '../../../models/Pagination';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, CircularProgress, Paper, Typography, Avatar, TableContainer, Stack, Collapse } from '@mui/material';
import PaginationButtons from '../../PaginationButtons';
import { formattedCurrency, nonZeroNumber } from '../../../utils/StringUtils';
import { ISTableTitle, ISTableHeaderCell, ISTableButton, ISTableRow, ISTableCell } from '../../Utils/ISTable';
import { SaveAltOutlined, KeyboardDoubleArrowRightOutlined, Inventory2Outlined, ThumbUpAltOutlined, LocalShippingOutlined, ThumbDownAltOutlined, ArrowDropUpOutlined, ArrowDropDownOutlined } from '@mui/icons-material';
import moment from 'moment';
import { useThemeContext } from '../../../theme/ThemeContext';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next'


interface StatusOrder {
  name: string
  hide?: boolean
  noDate?: boolean
  color?: string
  icon?: React.ReactNode
  adic?: string
}

export const tableToStatusOrder = (data: string, codigo_transporte?: number): string => {
  switch (data.toLowerCase()) {
    case "ingresado": return "admitted"
    case "en preparación": return "preparing"
    case "en distribución (con pendientes)": return codigo_transporte === 9999 ? "pickUp with pending" : "distributing with pending"
    case "en distribución (sin pendientes)": return codigo_transporte === 9999 ? "pickUp without pending" : "distributing without pending"
    case "cancelado": return "cancelled"
    case "listo para retirar (con pendientes)": return "pickUp with pending"
    case "listo para retirar (sin pendientes)": return "pickUp without pending"
    case "entregado (con pendientes)": return "delivered with pending"
    case "entregado (sin pendientes)": return "delivered without pending"
    case "otro": return "noStatus"
  }
  return ''
}

export const statusOrderToTable = (data: string): string => {
  switch (data.toLowerCase()) {
    case "admitted": return "ingresado"
    case "preparing": return "en preparacion"
    case "distributing with pending": return "en distribucion (con pendientes)"
    case "distributing without pending": return "en distribucion (sin pendientes)"
    case "cancelled": return "cancelado"
    case "pickUp with pending": return "listo para retirar (con pendientes)"
    case "pickUp without pending": return "listo para retirar (sin pendientes)"
    case "delivered with pending": return "entregado (con pendientes)"
    case "delivered without pending": return "entregado (sin pendientes)"
    case "noStatus": return "otro"
  }
  return ''
}

export const statusOrder = (data: string): StatusOrder => {
  switch (data) {
    case "admitted": return { name: "admitted", color: "gray", icon: <SaveAltOutlined /> }
    case "preparing": return { name: "preparing", color: "orange", icon: <Inventory2Outlined /> }
    case "distributing with pending": return { name: "distributing", color: "mediumseagreen", icon: <KeyboardDoubleArrowRightOutlined />, adic: 'withPend' }
    case "distributing without pending": return { name: "distributing", color: "green", icon: <KeyboardDoubleArrowRightOutlined /> }
    case "distributing": return { name: "distributing", color: "green", icon: <KeyboardDoubleArrowRightOutlined /> }
    case "cancelled": return { name: "cancelled", hide: true, color: "red", icon: <ThumbDownAltOutlined /> }
    case "pickUp with pending": return { name: "pickUp", color: "lightblue", icon: <ThumbUpAltOutlined />, adic: 'withPend' }
    case "pickUp without pending": return { name: "pickUp", color: "blue", icon: <ThumbUpAltOutlined /> }
    case "pickUp": return { name: "pickUp", color: "blue", icon: <ThumbUpAltOutlined /> }
    case "delivered": return { name: "delivered", color: "#008457", icon: <LocalShippingOutlined /> }
    case "delivered with pending": return { name: "delivered", color: "darkolivegreen", icon: <LocalShippingOutlined />, adic: 'withPend' }
    case "delivered without pending": return { name: "delivered", color: "#008457", icon: <LocalShippingOutlined /> }
  }
  return { name: 'noStatus', color: 'red' }
}

export const statusOrderNames: StatusOrder[] = [
  { name: "admitted" },
  { name: "preparing", noDate: true },
  { name: "pickUp", hide: true },
  { name: "distributing", hide: false },
  { name: "cancelled", hide: true },
  { name: "delivered" }]

export const statusOrderNamesTranslated = () =>
  statusOrderNames.map(name => t(`order:${name.name}`).toString())

export interface Row {
  data: Order
  action?: 'create' | 'update' | 'error';
}

interface Props {
  rows: Row[];
  loading: boolean;
  titleStatus?: string
  pagination?: Pagination;
  fullInfo?: boolean;
  adminInfo?: boolean;
  showDateStatus?: boolean;
  isChaco?: boolean | null;
  toDeliver?: boolean | null;
  onChangePage?: (page: number) => void;
  onClickInfo?: (order: Order) => void;
}

export const Orders: React.FC<Props> = (props) => {
  const { t } = useTranslation('order')
  const { theme } = useThemeContext()
  const [showFullInfo, setShowFullInfo] = useState<boolean>(!props.adminInfo)

  const mapActionToColor = (action?: string) => {
    switch (action) {
      case 'create': return 'green';
      case 'update': return 'yellow';
      case 'error': return 'red';
      default: return 'default';
    }
  }

  const tooltipText = (text: string) =>
    <Paper elevation={2} sx={{ padding: "10px" }}>
      <Typography >{text}</Typography>
    </Paper>

  const rowsToShow = /*!props.fullInfo ? props.rows.slice(0, 5) :*/ props.rows

  return (
    <Stack
      style={{ display: 'flex', flexDirection: 'column', height: props.fullInfo ? !props.adminInfo ? 'calc(100% - var(--header-height)' : '' : '100%', borderRadius: "10px", margin: "10px", minWidth: props.fullInfo ? "500px" : "700px" }}
      onClick={(event: FormEvent) => { event.preventDefault(); event.stopPropagation(); props.adminInfo ? setShowFullInfo(!showFullInfo) : setShowFullInfo(true) }}>
      <ISTableTitle title={!props.fullInfo ? t('lastrecords') : t('title')}
        sx={{ display: "flex", flexDirection: "row", alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack sx={{ display: "flex", flexDirection: "row" }}>
          {props.adminInfo && showFullInfo ? <ArrowDropUpOutlined sx={{ cursor: 'pointer', color: theme.palette.success.dark }} /> :
            props.adminInfo ? <ArrowDropDownOutlined sx={{ cursor: 'pointer', color: theme.palette.success.dark }} /> : <></>}
        </Stack>
      </ISTableTitle>
      <Collapse style={{ padding: props.adminInfo ? "10px" : 'inherit' }} in={props.adminInfo ? showFullInfo : true}>
        <TableContainer sx={{ overflowX: 'auto', height: !props.fullInfo ? '275px' : '100%' }}>
          {!props.loading &&
            <Table size="medium" stickyHeader={props.fullInfo}>
              <TableHead>
                <TableRow selected={true}>
                  <ISTableHeaderCell align='center'>{t('date')}</ISTableHeaderCell>
                  <ISTableHeaderCell align='center'>{t('number')}</ISTableHeaderCell>
                  <ISTableHeaderCell align='center'>{t('status')}</ISTableHeaderCell>
                  {props.fullInfo && <ISTableHeaderCell align='center'>{t('detail')}</ISTableHeaderCell>}
                  {!props.fullInfo && <ISTableHeaderCell align='right'>{t('total')}</ISTableHeaderCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {!props.loading &&
                  rowsToShow && rowsToShow.map((row, index) => {
                    const status = row.data.estados && row.data.estados?.length > 0 &&
                      (row.data.estados[0].estado_pedido ? tableToStatusOrder(row.data.estados[0].estado_pedido, row.data.codigo_transporte) : t('noStatus'))
                    return (
                      <ISTableRow key={index} color={mapActionToColor(row.action)}
                        sx={{ cursor: !props.fullInfo ? 'pointer' : 'default' }}
                        onClick={(e) => { e.stopPropagation(); !props.fullInfo && props.onClickInfo && props.onClickInfo(row.data) }}>
                        <TableCell align='center'>{moment(row.data.fecha).tz("America/Argentina/Buenos_Aires").format('DD/MM/YYYY')}</TableCell>
                        <TableCell align='center'>{row.data.pedido_numero}</TableCell>
                        {status && !props.fullInfo &&
                          <ISTableCell align='center'
                            tooltip={tooltipText(`${t(`${statusOrder(status)?.name}`)} ${statusOrder(status)?.adic ? t(`${statusOrder(status)?.adic}`) : ''}`)} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Paper
                              sx={{
                                maxHeight: '25px', minWidth: props.fullInfo ? "200px" : '110px', maxWidth: props.fullInfo ? "200px" : "110px", borderRadius: "15px", color: theme.palette.success.dark, textTransform: 'none',
                                backgroundColor: statusOrder(status)?.color
                              }}>
                              <Typography color='inherit' variant={props.fullInfo ? "body2" : "caption"}>{t(`${statusOrder(status)?.name}`)}</Typography>
                            </Paper>
                          </ISTableCell>}
                        {status && props.fullInfo && <OrderStatuses row={row.data} showDate={props.showDateStatus} isChaco={props.isChaco} />}
                        {!props.fullInfo && <TableCell align='right'>{formattedCurrency(row.data.importe_total_comprobante)}</TableCell>}
                        {props.fullInfo &&
                          <TableCell align='center'>
                            <ISTableButton text={t('viewDetail')}
                              onClick={(e) => { e.stopPropagation(); props.onClickInfo && props.onClickInfo(row.data) }}>
                            </ISTableButton>
                          </TableCell>}
                      </ISTableRow>
                    )
                  })
                }
              </TableBody>
            </Table>
          }
          {props.loading &&
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={25} />
            </div>
          }
          {props.fullInfo && <PaginationButtons pagination={props.pagination} changePage={props.onChangePage} />}
        </TableContainer>
      </Collapse>
    </Stack>
  );
}

interface OrderStatusesProps {
  row: Order;
  showDate?: boolean
  isChaco?: boolean | null
}

export const OrderStatuses: React.FC<OrderStatusesProps> = (props) => {
  const { t } = useTranslation('order')
  const { theme } = useThemeContext()

  return (
    <TableCell sx={{ alignItems: 'center', display: 'flex', justifyContent: props.isChaco ? 'space-evenly' : 'space-between', flexDirection: "row" }}>

      {statusOrderNames.map(({ name, hide, noDate }, index) => {
        hide = (props.row.codigo_transporte === 9999 && name.includes('distributing')) ? true :
          (props.row.codigo_transporte === 9999 && name.includes('pickUp')) ? false :
            (props.row.estados && props.row.estados.length > 0 && props.row.estados[0].estado_pedido.includes('ancel') && name.includes('delivered')) ? true : hide
        const estado = props.row.estados?.find(f => f.estado_pedido ?
          tableToStatusOrder(f.estado_pedido, props.row.codigo_transporte).includes(name) : undefined);
        hide = props.isChaco && !name.includes('admitted') && !name.includes('delivered') ? true : hide
        const adic = estado ? statusOrder(tableToStatusOrder(estado.estado_pedido, props.row.codigo_transporte)).adic : null
        return (
          ((estado || !hide) &&
            <Box key={index} sx={{
              display: 'flex', justifyContent: 'center', minWidth: '110px', flexDirection: "column", alignItems: 'center', borderBottomColor: 'black',
              borderBottomWidth: 1,
            }}>

              <Avatar sx={{
                color: estado ? theme.palette.success.dark : '#202020',
                backgroundColor: estado ? theme.palette.success.main : '#cccccc'
              }}>
                {statusOrder(estado ? tableToStatusOrder(estado.estado_pedido, props.row.codigo_transporte) : name).icon}
              </Avatar>
              <Typography color='inherit' variant="caption">{t(`${estado ? statusOrder(tableToStatusOrder(estado.estado_pedido, props.row.codigo_transporte)).name : name}`)}</Typography>
              {adic && <Typography color='inherit' variant="caption">{' ('}{t(`${adic}`)}{')'}</Typography>}
              {props.showDate && <Typography color='inherit' variant="caption">{estado ? !noDate ? props.row.estados && moment(props.row.estados[0].fecha_actualizacion).tz("America/Argentina/Buenos_Aires").format('DD/MM/YYYY') : `\u00A0` : `\u00A0`}</Typography>}
            </Box>)
        )
      })}

    </TableCell>
  )
}

export const OrderDetails: React.FC<Props> = (props) => {
  const { t } = useTranslation('order')

  const getPriority = (item) => {
    const detalle = item.data.detalle;

    // 1. Prioridad 4: tiene piezas_pendientes_remito no nulas
    if (detalle.piezas_pendientes_remito !== null) return 4;

    // 2. Prioridad 3: cálculo de pendientes > 0
    const pendientes = Number(detalle.piezas_pedidas) -
      Number(detalle.piezas_entregadas) -
      Number(detalle.piezas_pendientes_remito);
    if (pendientes > 0) return 3;

    // 3. Prioridad 2: tiene piezas_canceladas no nulas y > 0
    if (detalle.piezas_canceladas !== null && Number(detalle.piezas_canceladas) > 0) return 2;

    // 3.5 Prioridad 2: tiene piezas_canceladas no nulas y > 0
    if (detalle.cantidad_nota_credito !== null && Number(detalle.cantidad_nota_credito) > 0) return 2;

    // 4. Prioridad 1: todo lo demás (cumple condición 1)
    return 1;
  };

  const tooltipText = (text: string) =>
    <Paper elevation={2} sx={{ padding: "10px" }}>
      <Typography >{text}</Typography>
    </Paper>


  return (
    <Stack style={{ display: 'flex', flexDirection: 'column', height: '50%', borderRadius: "10px", margin: "10px", minWidth: "500px" }}>
      <ISTableTitle title={`${t('titleDetail')}`} />
      <TableContainer sx={{ overflowX: 'auto', height: '100%' }}>
        <Table size="medium" stickyHeader >
          <TableHead>
            <TableRow selected={true}>
              <ISTableHeaderCell>{t('brand')}</ISTableHeaderCell>
              <ISTableHeaderCell>{t('description')}</ISTableHeaderCell>
              <ISTableHeaderCell align='center'>{t('code')}</ISTableHeaderCell>
              <ISTableHeaderCell align='right'>{t('unitPrice')}</ISTableHeaderCell>

              <ISTableHeaderCell align='right'>{t('orderedPieces')}</ISTableHeaderCell>
              <ISTableHeaderCell align='right'>{t('deliveredPieces')}</ISTableHeaderCell>
              {(!props.isChaco || props.toDeliver ) && <ISTableHeaderCell align='right'>{t('toDeliverPieces')}</ISTableHeaderCell>}

              <ISTableHeaderCell align='right'>{t('canceledPieces')}</ISTableHeaderCell>
              <ISTableHeaderCell align='right'>{t('pendingPieces')}</ISTableHeaderCell>
              <ISTableHeaderCell align='right'>{t('totalSum')}</ISTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!props.loading &&
              props.rows.sort((a, b) => {
                if (!a || !b) return 0;

                // Ordena por prioridad (descendente) y luego por código
                const priorityDiff = getPriority(b) - getPriority(a);
                return priorityDiff !== 0 ? priorityDiff : a.data.codigo_articulo.localeCompare(b.data.codigo_articulo);
              })
                .map((row, index) => {
                  const bold = Number(row.data.detalle.piezas_pedidas as unknown).toFixed(0) !== (Number(row.data.detalle.piezas_entregadas) - Number(row.data.detalle.piezas_pendientes_remito)).toFixed(0)
                  return (
                    <ISTableRow color='default' key={index}>
                      <TableCell style={{ fontWeight: bold ? 'bold' : 'inherit' }} >{row.data.marca}</TableCell>
                      <TableCell style={{ fontWeight: bold ? 'bold' : 'inherit' }}>{row.data.descripcion}</TableCell>
                      <TableCell style={{ fontWeight: bold ? 'bold' : 'inherit' }} align='center'>{row.data.codigo_articulo}</TableCell>
                      <TableCell style={{ fontWeight: bold ? 'bold' : 'inherit' }} align='right'>{formattedCurrency(row.data.precio_unitario, 3)}</TableCell>
                      <TableCell style={{ fontWeight: bold ? 'bold' : 'inherit' }} align='right'>{Number(row.data.cantidad as unknown).toFixed(0)}</TableCell>

                      <TableCell style={{ fontWeight: bold ? 'bold' : 'inherit' }} align='right'>{nonZeroNumber(Number(row.data.detalle.piezas_entregadas as unknown))}</TableCell>

                      {(!props.isChaco || props.toDeliver ) && <TableCell style={{ fontWeight: bold ? 'bold' : 'inherit' }} align='right'>{nonZeroNumber(Number(row.data.detalle.piezas_pendientes_remito as unknown))}</TableCell>}

                      <ISTableCell sx={{ position: 'relative' }} placement='right' tooltip={row.data.detalle.cantidad_nota_credito ? tooltipText(`${row.data.detalle.piezas_canceladas ? row.data.detalle.cantidad_nota_credito : ''} ${t('byNC')}`) : undefined} style={{ fontWeight: bold ? 'bold' : 'inherit' }}
                        align='right'>
                        {row.data.detalle.cantidad_nota_credito ||
                          (row.data.detalle.cantidad_nota_credito && row.data.detalle.piezas_canceladas) ? (
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span>{nonZeroNumber(Number(row.data.detalle.piezas_canceladas as unknown) + Number(row.data.detalle.cantidad_nota_credito as unknown))}</span>
                            <span style={{ display: 'inline-block', width: '100%' }}>
                              <div
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  right: 0,
                                  width: 0,
                                  height: 0,
                                  borderStyle: 'solid',
                                  borderWidth: '0 12px 12px 0',
                                  borderColor: row.data.detalle.piezas_canceladas ? 'transparent #f69012 transparent transparent' : 'transparent red transparent transparent',
                                }}
                              />
                            </span>
                          </div>
                        ) : <><span>{nonZeroNumber(Number(row.data.detalle.piezas_canceladas as unknown) + Number(row.data.detalle.cantidad_nota_credito as unknown))}</span></>
                        }
                      </ISTableCell>

                      <TableCell style={{ fontWeight: bold ? 'bold' : 'inherit' }} align='right'>{nonZeroNumber(Number(row.data.detalle.piezas_pedidas as unknown) - Number(row.data.detalle.piezas_entregadas as unknown) - Number(row.data.detalle.piezas_pendientes_remito as unknown))}</TableCell>

                      <TableCell style={{ fontWeight: bold ? 'bold' : 'inherit' }} align='right'>{formattedCurrency(row.data.importe)}</TableCell>
                    </ISTableRow>
                  )
                })
            }
          </TableBody>
        </Table>
        {props.loading &&
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={25} />
          </div>
        }
        {<PaginationButtons pagination={props.pagination} />}
      </TableContainer>
    </Stack>
  );
}
